import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, Gem, HeartHandshake, Sparkles, Gift } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { IMAGES, BRAND } from "@/lib/constants";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import FestiveBanner from "@/components/FestiveBanner";
import Testimonials from "@/components/Testimonials";
import { categoryName } from "@/lib/constants";

const MANIFESTO = [
  {
    num: "01",
    title: "The Sacred Gesture",
    text: "A gift is never just an object. It is the pause before a meeting, the weight of gratitude after a milestone, the quiet way a brand says: you matter to us. We treat every hamper as a sentence in that conversation.",
  },
  {
    num: "02",
    title: "Artisanal Craft",
    text: "Every Gratiora piece is composed by hand in our Delhi NCR studio — estate teas, hand-poured candles, brass objects, letterpress cards. Nothing generic survives our curation table.",
  },
  {
    num: "03",
    title: "Tailored Identity",
    text: "Your logo in foil. Your colours in ribbon and wax. Your story in the card inside the lid. We build gifts that could only have come from you — at quantities from twenty-five to twenty-five thousand.",
  },
];

const TRUST = [
  { icon: Gem, title: "Premium Quality", text: "Materials sourced from artisans and estates we know by name." },
  { icon: HeartHandshake, title: "Thoughtful Gestures", text: "Every curation begins with who receives it, not what fills it." },
  { icon: Sparkles, title: "Made for Memories", text: "Keepsake packaging designed to live on long after the contents." },
  { icon: Gift, title: "Wrapped with Care", text: "Hand-finished wrapping, wax seals and ribbons on every single unit." },
];

const STEPS = [
  { num: "01", title: "Brief", text: "Share your occasion, audience, budget and brand. We respond within one business day with directions." },
  { num: "02", title: "Sampling", text: "We curate, mock up and send a physical or photographic sample — your logo, your palette, your message." },
  { num: "03", title: "Production", text: "Approved samples move to hand assembly. Every unit is checked, wrapped and sealed in our studio." },
  { num: "04", title: "Delivery", text: "Pan-India delivery to one address or a thousand, tracked and timed to your event." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 4]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.09)_0%,rgba(250,248,245,0)_70%)]" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center w-full pt-32 pb-20">
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="eyebrow flex items-center gap-3"
          >
            <span className="inline-block h-px w-10 bg-gold-brass" />
            Delhi NCR · Bespoke Gifting Studio
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="mt-7 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-ink text-balance"
          >
            <MaskedLine delay={0.35}>The art of</MaskedLine>
            <MaskedLine delay={0.5} className="italic text-gold-brass">bespoke gifting,</MaskedLine>
            <MaskedLine delay={0.65}>beautifully boxed.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-md text-base text-ink-muted leading-relaxed"
          >
            Corporate hampers, welcome kits and wedding favours — hand-curated,
            branded to your identity, and delivered with obsessive care. No price
            lists. Every gift begins with a conversation.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <Link
              to="/enquire"
              data-testid="hero-cta-quote"
              className="inline-flex items-center gap-2 bg-ink text-cream px-8 py-4 text-sm tracking-wide hover:bg-gold-brass transition-colors duration-400 group"
            >
              Request a Quote
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/catalogue"
              data-testid="hero-cta-catalogue"
              className="text-sm text-ink gold-underline-btn font-medium"
            >
              Explore the Catalogue
            </Link>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-[340px] sm:max-w-[400px]"
          >
            <div className="absolute -inset-3 border border-gold/30 rounded-t-[999px]" />
            <motion.div style={{ y, rotate }} className="overflow-hidden rounded-t-[999px] shadow-[0_40px_80px_rgba(28,25,23,0.18)]">
              <img
                src={BRAND.elephant}
                alt="The Heritage Elephant Trunk — royal blue Gratiora gift trunk"
                className="w-full aspect-[3/4] object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              className="absolute -bottom-6 -left-6 bg-white/90 backdrop-blur-md border border-gold/20 px-5 py-4 shadow-lg"
            >
              <p className="font-serif text-2xl text-ink">2,500+</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mt-0.5">Hampers delivered</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-ink/40"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

function Audience() {
  const cards = [
    {
      title: "Corporate Gifting",
      text: "Bulk hampers, welcome kits, appreciation boxes and festive gifting for teams and clients.",
      to: "/catalogue?audience=corporate",
      image: BRAND.navy,
    },
    {
      title: "Weddings & Personal",
      text: "Favours, festive and signature collections for celebrations that deserve keepsakes.",
      to: "/catalogue?audience=personal",
      image: BRAND.wedding,
    },
  ];
  return (
    <section className="py-20 lg:py-32 px-6 lg:px-12 max-w-7xl mx-auto">
      <Reveal>
        <p className="eyebrow">Who we gift for</p>
        <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">Choose your occasion</h2>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-2 gap-6">
        {cards.map((card, i) => (
          <Reveal key={card.title} delay={i * 0.12}>
            <Link
              to={card.to}
              data-testid={`audience-card-${i}`}
              className="group relative block overflow-hidden aspect-[16/11]"
            >
              <img
                src={card.image}
                alt={card.title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-deep/85 via-ink-deep/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-8">
                <h3 className="font-serif text-2xl sm:text-3xl text-cream">{card.title}</h3>
                <p className="mt-2 text-sm text-cream/70 max-w-sm leading-relaxed">{card.text}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm text-gold font-medium">
                  Browse collection
                  <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section id="story" className="py-20 lg:py-32 bg-cream-dark/60 border-y border-gold/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal className="lg:sticky lg:top-32">
            <p className="eyebrow">The Art of Gifting</p>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight text-balance">
              A manifesto in three chapters
            </h2>
            <div className="mt-10 overflow-hidden">
              <img src={BRAND.board} alt="Gratiora brand packaging — bag, box and monogram" loading="lazy" className="w-full aspect-[4/5] object-cover" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 flex flex-col justify-center">
          {MANIFESTO.map((ch, i) => (
            <Reveal key={ch.num} delay={i * 0.1} data-testid={`manifesto-chapter-${ch.num}`}>
              <div data-testid={`manifesto-chapter-${ch.num}`} className={`py-10 ${i > 0 ? "border-t border-gold/15" : ""}`}>
                <div className="flex items-start gap-6">
                  <span className="font-mono text-sm text-gold-brass pt-2">{ch.num}</span>
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl font-medium text-ink">{ch.title}</h3>
                    <p className="mt-3 text-base text-ink-muted leading-relaxed max-w-xl">{ch.text}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Featured() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  if (featured.length === 0) return null;

  return (
    <section className="py-20 lg:py-32 px-6 lg:px-12 max-w-7xl mx-auto">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow">Featured Collections</p>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-ink tracking-tight">
            Pieces we're proud of
          </h2>
        </div>
        <Link to="/catalogue" className="text-sm text-gold-brass gold-underline-btn font-medium">
          View full catalogue
        </Link>
      </Reveal>

      <div data-testid="featured-collections-grid" className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {featured.slice(0, 5).map((p, i) => (
          <Reveal key={p.id} delay={i * 0.08} className={i === 0 ? "md:col-span-2 md:row-span-2" : ""}>
            <Link
              to={`/product/${p.id}`}
              data-testid={`featured-card-${i}`}
              className={`group relative block overflow-hidden ${i === 0 ? "aspect-[4/3] md:aspect-auto md:h-full" : "aspect-[4/3]"}`}
            >
              <img
                src={imgUrl(p.image)}
                alt={p.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-deep/80 via-ink-deep/15 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                <p className="eyebrow !text-gold/90">{categoryName(p.category)}</p>
                <h3 className={`mt-2 font-serif text-cream ${i === 0 ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"}`}>
                  {p.name}
                </h3>
                <span className="mt-3 inline-flex items-center gap-2 text-sm text-gold opacity-0 translate-y-2 transition-[opacity,transform] duration-400 group-hover:opacity-100 group-hover:translate-y-0">
                  View & request quote <ArrowUpRight size={14} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Trust() {
  return (
    <section className="py-20 lg:py-28 px-6 lg:px-12 max-w-7xl mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {TRUST.map((t, i) => (
          <Reveal key={t.title} delay={i * 0.08}>
            <div data-testid={`trust-signal-${i}`} className="group border border-gold/15 bg-white p-8 h-full hover:border-gold/40 hover:shadow-[0_20px_40px_rgba(212,175,55,0.08)] transition-[border-color,box-shadow] duration-500">
              <t.icon size={26} strokeWidth={1.5} className="text-gold-brass" />
              <h3 className="mt-5 font-serif text-xl text-ink">{t.title}</h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">{t.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Process() {
  return (
    <section id="process" className="py-20 lg:py-32 bg-ink-deep text-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="eyebrow !text-gold/80">How Customization Works</p>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-balance">
            From a one-line brief to a thousand wrapped boxes
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.1}>
              <div data-testid={`customization-step-${i}`} className="relative">
                <span className="font-serif text-6xl font-light text-gold/25">{s.num}</span>
                <div className="mt-4 h-px w-full bg-gradient-to-r from-gold/50 to-transparent" />
                <h3 className="mt-5 font-serif text-xl sm:text-2xl">{s.title}</h3>
                <p className="mt-3 text-sm text-cream/60 leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-16">
          <Link
            to="/enquire"
            className="inline-flex items-center gap-2 bg-gold text-ink px-8 py-4 text-sm font-medium tracking-wide hover:bg-gold-hover transition-colors duration-400 group"
          >
            Start your brief
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="py-24 lg:py-36 px-6 lg:px-12">
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <p className="eyebrow">Every gift begins with a conversation</p>
          <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.1] text-balance">
            Let's craft something <span className="italic text-gold-brass">unforgettable.</span>
          </h2>
          <Link
            to="/enquire"
            className="mt-10 inline-flex items-center gap-2 bg-ink text-cream px-10 py-5 text-sm tracking-wide hover:bg-gold-brass transition-colors duration-400 group"
          >
            Request a Quote
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <Marquee />
      <FestiveBanner />
      <Audience />
      <Manifesto />
      <Featured />
      <Trust />
      <Testimonials />
      <Process />
      <CtaBand />
    </main>
  );
}
